// src/tabs/RelatoriosSubTab.js
import React, { useState, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer, 
  LineChart, 
  Line 
} from "recharts";
import { 
  TrendingUp, 
  TrendingDown, 
  Download, 
  CheckCircle, 
  AlertCircle, 
  Activity, 
  DollarSign, 
  Layers,
  PieChart
} from "lucide-react";
import { formatCentavos } from "../utils/moneyUtils";

export default function RelatoriosSubTab({ finance }) {
  const [activeTab, setActiveTab] = useState("dashboard"); // 'dashboard' | 'pivot' | 'auditoria'
  const [selAno, setSelAno] = useState(new Date().getFullYear());
  const [selCentro, setSelCentro] = useState("");
  const [resumos, setResumos] = useState([]);
  const [lotesExtratos, setLotesExtratos] = useState([]);
  const [loadingLocal, setLoadingLocal] = useState(false);

  const anosDisponiveis = [2025, 2026, 2027, 2028];

  const fetchDados = async () => {
    try {
      setLoadingLocal(true);
      if (typeof finance.getResumosAno === "function") {
        const res = await finance.getResumosAno(selAno);
        setResumos(res || []);
      }
      if (typeof finance.getAuditoriaConciliacao === "function") {
        const lotes = await finance.getAuditoriaConciliacao(selAno);
        setLotesExtratos(lotes || []);
      }
    } catch (err) {
      console.warn("Erro ao buscar resumos consolidados:", err.message);
    } finally {
      setLoadingLocal(false);
    }
  };

  useEffect(() => {
    fetchDados();
  }, [selAno]);

  // ─── FILTRAGEM DOS RESUMOS PELO CENTRO DE CUSTO SELECIONADO ─────────
  const resumosFiltrados = useMemo(() => {
    if (!selCentro) return resumos;
    return resumos.filter(r => r.centro_custo === selCentro);
  }, [resumos, selCentro]);

  // ─── CÁLCULOS DOS FLUXOS MENSAIS E ANUAIS ───────────────────────────
  const { chartData, totals, pivotData } = useMemo(() => {
    const meses = Array.from({ length: 12 }, (_, i) => i + 1);
    
    // Inicializa estrutura de dados mensais para os gráficos
    const mesesMap = meses.reduce((acc, m) => {
      acc[m] = { mes: m, receitas: 0, despesas: 0 };
      return acc;
    }, {});

    // Inicializa estrutura de dados por categoria para o Pivot
    const pivot = {
      receitas: {},
      despesas: {}
    };

    let totalReceitas = 0;
    let totalDespesas = 0;

    resumosFiltrados.forEach(r => {
      const cat = r.expand?.categoria;
      if (!cat) return;

      const mes = r.mes;
      const pago = r.total_pago_centavos || 0; // Saídas no ledger são negativas

      // Agrupa no pivot por categoria e mês
      const tipoCat = cat.tipo; // 'receita' | 'despesa'
      const catPivotMap = pivot[tipoCat === "receita" ? "receitas" : "despesas"];
      
      if (!catPivotMap[cat.id]) {
        catPivotMap[cat.id] = {
          nome: cat.nome,
          classificacao: cat.classificacao || "Outros Custos Operacionais",
          valores: meses.reduce((a, m) => { a[m] = 0; return a; }, {}),
          total: 0
        };
      }

      // Soma os valores. Despesas são guardadas negativas no Ledger, mas nos gráficos e pivot exibimos como positivas
      const absVal = Math.abs(pago);
      catPivotMap[cat.id].valores[mes] += absVal;
      catPivotMap[cat.id].total += absVal;

      if (tipoCat === "receita") {
        mesesMap[mes].receitas += absVal;
        totalReceitas += absVal;
      } else {
        mesesMap[mes].despesas += absVal;
        totalDespesas += absVal;
      }
    });

    // Formata o array para o Recharts, computando o acumulado do ano
    let saldoAcumulado = 0;
    const chart = meses.map(m => {
      const rec = mesesMap[m].receitas;
      const des = mesesMap[m].despesas;
      const saldoMes = rec - des;
      saldoAcumulado += saldoMes;
      
      const nomesMeses = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
      return {
        name: nomesMeses[m - 1],
        Receitas: rec / 100,
        Despesas: des / 100,
        "Resultado do Mês": saldoMes / 100,
        "Acumulado do Ano": saldoAcumulado / 100
      };
    });

    return {
      chartData: chart,
      totals: {
        receitas: totalReceitas,
        despesas: totalDespesas,
        liquido: totalReceitas - totalDespesas,
        eficiencia: totalReceitas > 0 ? ((totalReceitas - totalDespesas) / totalReceitas) * 100 : 0
      },
      pivotData: pivot
    };
  }, [resumosFiltrados]);

  // ─── AGREGADOR DA MATRIZ PIVOT ORGANIZADA POR CLASSIFICAÇÃO ─────────
  const pivotOrganizado = useMemo(() => {
    // Receitas
    const receitasList = Object.values(pivotData.receitas).sort((a, b) => a.nome.localeCompare(b.nome));

    // Despesas agrupadas por classificação contábil
    const despesasGrouped = {};
    Object.values(pivotData.despesas).forEach(cat => {
      const cls = cat.classificacao;
      if (!despesasGrouped[cls]) {
        despesasGrouped[cls] = [];
      }
      despesasGrouped[cls].push(cat);
    });

    // Ordena as categorias de despesa dentro de cada classificação
    Object.keys(despesasGrouped).forEach(cls => {
      despesasGrouped[cls].sort((a, b) => a.nome.localeCompare(b.nome));
    });

    return {
      receitas: receitasList,
      despesasByCls: despesasGrouped
    };
  }, [pivotData]);

  // ─── EXPORTADOR DE EXCEL (SHEETJS) ──────────────────────────────────
  const handleExportExcel = () => {
    const mesesLabels = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
    
    // Cabeçalho da Planilha
    const rows = [
      [`RELATÓRIO DE FLUXO DE CAIXA PIVOT — ANO ${selAno}`],
      [selCentro ? `Centro de Custo: ${finance.centrosCusto.find(cc => cc.id === selCentro)?.nome || "Outro"}` : "Centro de Custo: Geral (Todos)"],
      [],
      ["Categoria / Classificação", ...mesesLabels, "Total Acumulado"]
    ];

    // Auxiliar para computar soma horizontal e vertical
    const somaMensalReceitas = Array(13).fill(0); // 1-12 meses + index 12 para Total
    const somaMensalDespesas = Array(13).fill(0);

    // 1. Receitas
    rows.push(["1. RECEITAS DE CAIXA (REALIZADO)"]);
    pivotOrganizado.receitas.forEach(cat => {
      const cols = [];
      for (let m = 1; m <= 12; m++) {
        const val = cat.valores[m] / 100;
        cols.push(val);
        somaMensalReceitas[m - 1] += val;
      }
      const totalCat = cat.total / 100;
      cols.push(totalCat);
      somaMensalReceitas[12] += totalCat;
      rows.push([`   ${cat.nome}`, ...cols]);
    });

    // Subtotal Receitas
    rows.push(["Subtotal Receitas", ...somaMensalReceitas]);
    rows.push([]);

    // 2. Despesas por Classificação
    rows.push(["2. DESPESAS DE CAIXA (REALIZADO)"]);
    
    Object.entries(pivotOrganizado.despesasByCls).forEach(([cls, cats]) => {
      rows.push([`  [${cls}]`]);
      const somaCls = Array(13).fill(0);

      cats.forEach(cat => {
        const cols = [];
        for (let m = 1; m <= 12; m++) {
          const val = cat.valores[m] / 100;
          cols.push(val);
          somaCls[m - 1] += val;
          somaMensalDespesas[m - 1] += val;
        }
        const totalCat = cat.total / 100;
        cols.push(totalCat);
        somaCls[12] += totalCat;
        somaMensalDespesas[12] += totalCat;
        rows.push([`     ${cat.nome}`, ...cols]);
      });
      // Linha de subtotal da classificação
      rows.push([`  Subtotal ${cls}`, ...somaCls]);
    });

    // Total Geral Despesas
    rows.push(["Total Geral Despesas", ...somaMensalDespesas]);
    rows.push([]);

    // 3. Resultado Líquido
    const resultadoMensal = somaMensalReceitas.map((rec, i) => rec - somaMensalDespesas[i]);
    rows.push(["RESULTADO OPERACIONAL LÍQUIDO", ...resultadoMensal]);

    // Cria a planilha no formato SheetJS
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Pivot ${selAno}`);

    // Autoajustar largura de colunas para legibilidade
    const maxCols = 15;
    ws["!cols"] = Array.from({ length: maxCols }, () => ({ wch: 14 }));
    ws["!cols"][0] = { wch: 38 }; // Primeira coluna mais larga para nomes de categorias

    XLSX.writeFile(wb, `Pivot_Financeiro_Fluxo_Caixa_${selAno}.xlsx`);
  };

  // ─── ESTATÍSTICAS DE CONCILIAÇÃO BANCÁRIA ───────────────────────────
  const auditoriaStats = useMemo(() => {
    const contasMap = finance.contasBancarias.reduce((acc, c) => {
      acc[c.id] = {
        nome: c.nome,
        meses: Array.from({ length: 12 }, (_, i) => ({
          mes: i + 1,
          total: 0,
          conciliadas: 0,
          ignoradas: 0
        }))
      };
      return acc;
    }, {});

    lotesExtratos.forEach(lot => {
      const cId = lot.conta_bancaria;
      if (!contasMap[cId]) return;

      const dateStr = lot.periodo_inicio || lot.created;
      if (!dateStr) return;

      const mes = parseInt(dateStr.split("-")[1], 10);
      if (isNaN(mes) || mes < 1 || mes > 12) return;

      const stats = contasMap[cId].meses[mes - 1];
      stats.total += lot.total_transacoes || 0;
      stats.conciliadas += lot.total_conciliadas || 0;
      stats.ignoradas += lot.total_ignoradas || 0;
    });

    return Object.values(contasMap);
  }, [lotesExtratos, finance.contasBancarias]);

  return (
    <div className="flex-1 overflow-hidden flex flex-col bg-slate-50 relative">
      {/* Barra de Filtros e Abas de Sub-Visão */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 shrink-0 flex flex-col md:flex-row gap-4 items-center justify-between shadow-xs">
        
        {/* Switcher de Abas Internas */}
        <div className="flex bg-slate-100 rounded-lg p-0.5 self-start">
          <button
            onClick={() => setActiveTab("dashboard")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
              activeTab === "dashboard"
                ? "bg-white text-slate-900 shadow-xs"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <PieChart size={14} /> Dashboard Geral
          </button>
          <button
            onClick={() => setActiveTab("pivot")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
              activeTab === "pivot"
                ? "bg-white text-slate-900 shadow-xs"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <Layers size={14} /> Matriz Pivot (DASH)
          </button>
          <button
            onClick={() => setActiveTab("auditoria")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
              activeTab === "auditoria"
                ? "bg-white text-slate-900 shadow-xs"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <Activity size={14} /> Auditoria Bancária
          </button>
        </div>

        {/* Seleção de Ano & Centro de Custo */}
        <div className="flex items-center gap-2 w-full md:w-auto md:justify-end">
          <div className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-lg border border-slate-200">
            <span className="text-[10px] font-black uppercase text-slate-400">Ano:</span>
            <select
              value={selAno}
              onChange={(e) => setSelAno(parseInt(e.target.value, 10))}
              className="bg-transparent border-none text-xs font-bold text-slate-700 focus:ring-0 outline-none"
            >
              {anosDisponiveis.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-lg border border-slate-200">
            <span className="text-[10px] font-black uppercase text-slate-400">Centro:</span>
            <select
              value={selCentro}
              onChange={(e) => setSelCentro(e.target.value)}
              className="bg-transparent border-none text-xs font-bold text-slate-700 focus:ring-0 outline-none w-36"
            >
              <option value="">Geral (Todos)</option>
              {finance.centrosCusto.filter(c => c.ativo).map(c => (
                <option key={c.id} value={c.id}>{c.nome}</option>
              ))}
            </select>
          </div>

          {activeTab === "pivot" && (
            <button
              onClick={handleExportExcel}
              className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-xs transition-colors tracking-wide"
            >
              <Download size={14} /> Exportar Excel
            </button>
          )}
        </div>
      </div>

      {/* Conteúdo Principal com scroll */}
      <div className="flex-1 overflow-auto p-6">
        {loadingLocal ? (
          <div className="flex flex-col items-center justify-center py-32 gap-2">
            <Activity className="animate-spin text-blue-600" size={32} />
            <span className="text-xs font-black text-slate-500 uppercase tracking-widest">Carregando dados agregados...</span>
          </div>
        ) : (
          <>
            {/* ─── VISÃO: DASHBOARD GERAL ────────────────────────────────────── */}
            {activeTab === "dashboard" && (
              <div className="space-y-6">
                
                {/* Grid de Kpis */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs flex items-center gap-4">
                    <div className="bg-emerald-50 text-emerald-600 p-3 rounded-lg">
                      <DollarSign size={20} />
                    </div>
                    <div>
                      <span className="block text-[10px] font-black uppercase text-slate-400">Receitas Acumuladas</span>
                      <span className="text-lg font-black text-slate-800">{formatCentavos(totals.receitas)}</span>
                    </div>
                  </div>

                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs flex items-center gap-4">
                    <div className="bg-rose-50 text-rose-600 p-3 rounded-lg">
                      <TrendingDown size={20} />
                    </div>
                    <div>
                      <span className="block text-[10px] font-black uppercase text-slate-400">Despesas Acumuladas</span>
                      <span className="text-lg font-black text-rose-600">{formatCentavos(totals.despesas)}</span>
                    </div>
                  </div>

                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs flex items-center gap-4">
                    <div className={`${totals.liquido >= 0 ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"} p-3 rounded-lg`}>
                      <TrendingUp size={20} />
                    </div>
                    <div>
                      <span className="block text-[10px] font-black uppercase text-slate-400">Resultado Líquido</span>
                      <span className={`text-lg font-black ${totals.liquido >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                        {totals.liquido < 0 ? "-" : ""}{formatCentavos(Math.abs(totals.liquido))}
                      </span>
                    </div>
                  </div>

                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs flex items-center gap-4">
                    <div className="bg-blue-50 text-blue-600 p-3 rounded-lg">
                      <Activity size={20} />
                    </div>
                    <div>
                      <span className="block text-[10px] font-black uppercase text-slate-400">Eficiência Operacional</span>
                      <span className="text-lg font-black text-slate-800">{totals.eficiencia.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>

                {/* Grid de Gráficos */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  
                  {/* Gráfico 1: Receitas vs Despesas */}
                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs">
                    <h5 className="font-bold text-xs uppercase tracking-wider text-slate-500 mb-4 flex items-center gap-2">
                      <PieChart size={14} className="text-blue-500" /> Fluxo de Caixa Mensal (Receitas vs Despesas)
                    </h5>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                          <XAxis dataKey="name" stroke="#64748B" fontSize={10} tickLine={false} />
                          <YAxis stroke="#64748B" fontSize={10} tickLine={false} />
                          <Tooltip 
                            formatter={(value) => [`R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`, undefined]} 
                            contentStyle={{ borderRadius: "8px", borderColor: "#E2E8F0" }}
                          />
                          <Legend wrapperStyle={{ fontSize: "11px", pt: 10 }} />
                          <Bar dataKey="Receitas" fill="#10B981" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="Despesas" fill="#F43F5E" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Gráfico 2: Evolução Acumulada */}
                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs">
                    <h5 className="font-bold text-xs uppercase tracking-wider text-slate-500 mb-4 flex items-center gap-2">
                      <TrendingUp size={14} className="text-blue-500" /> Evolução do Resultado Líquido Acumulado
                    </h5>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 10, right: 15, left: 10, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                          <XAxis dataKey="name" stroke="#64748B" fontSize={10} tickLine={false} />
                          <YAxis stroke="#64748B" fontSize={10} tickLine={false} />
                          <Tooltip 
                            formatter={(value) => [`R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`, undefined]}
                            contentStyle={{ borderRadius: "8px", borderColor: "#E2E8F0" }}
                          />
                          <Legend wrapperStyle={{ fontSize: "11px" }} />
                          <Line type="monotone" dataKey="Acumulado do Ano" stroke="#2563EB" strokeWidth={3} activeDot={{ r: 6 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                </div>

              </div>
            )}

            {/* ─── VISÃO: MATRIZ PIVOT (DASH) ────────────────────────────────── */}
            {activeTab === "pivot" && (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs flex flex-col">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-[1250px]">
                    <thead>
                      <tr className="bg-slate-900 text-white">
                        <th className="px-4 py-3 text-[10px] font-black uppercase tracking-wider w-80">Categoria / Classificação</th>
                        {["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"].map(m => (
                          <th key={m} className="px-3 py-3 text-[10px] font-black uppercase text-center">{m}</th>
                        ))}
                        <th className="px-4 py-3 text-[10px] font-black uppercase text-right w-36">Total Acumulado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-xs">
                      
                      {/* 1. SEÇÃO DE RECEITAS */}
                      <tr className="bg-slate-50 font-black text-slate-800">
                        <td colSpan={14} className="px-4 py-2 uppercase tracking-wider text-[10px] text-emerald-700">1. Receitas de Caixa</td>
                      </tr>
                      {pivotOrganizado.receitas.map(cat => (
                        <tr key={cat.nome} className="hover:bg-slate-50/50">
                          <td className="px-4 py-2.5 font-bold text-slate-700">{cat.nome}</td>
                          {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                            <td key={m} className="px-3 py-2.5 text-center font-mono font-medium text-slate-600">
                              {cat.valores[m] > 0 ? formatCentavos(cat.valores[m]) : "—"}
                            </td>
                          ))}
                          <td className="px-4 py-2.5 text-right font-black text-emerald-600 font-mono">{formatCentavos(cat.total)}</td>
                        </tr>
                      ))}

                      {/* Subtotal Receitas */}
                      <tr className="bg-emerald-50/70 font-black text-emerald-800 border-t border-emerald-200">
                        <td className="px-4 py-3 text-xs uppercase tracking-wide">Subtotal Receitas</td>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => {
                          const somaMes = pivotOrganizado.receitas.reduce((sum, cat) => sum + cat.valores[m], 0);
                          return (
                            <td key={m} className="px-3 py-3 text-center font-mono">
                              {somaMes > 0 ? formatCentavos(somaMes) : "—"}
                            </td>
                          );
                        })}
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {formatCentavos(pivotOrganizado.receitas.reduce((sum, cat) => sum + cat.total, 0))}
                        </td>
                      </tr>

                      {/* 2. SEÇÃO DE DESPESAS POR CLASSIFICAÇÃO */}
                      <tr className="bg-slate-50 font-black text-slate-800">
                        <td colSpan={14} className="px-4 py-2 uppercase tracking-wider text-[10px] text-rose-700">2. Despesas de Caixa</td>
                      </tr>

                      {Object.entries(pivotOrganizado.despesasByCls).map(([cls, cats]) => {
                        const subSomaMeses = Array.from({ length: 12 }, (_, i) => i + 1).reduce((acc, m) => {
                          acc[m] = cats.reduce((sum, cat) => sum + cat.valores[m], 0);
                          return acc;
                        }, {});
                        const subTotalCls = cats.reduce((sum, cat) => sum + cat.total, 0);

                        return (
                          <React.Fragment key={cls}>
                            <tr className="bg-slate-100/60 font-bold text-slate-700">
                              <td colSpan={14} className="px-4 py-1.5 text-[9px] uppercase tracking-wide text-slate-500 font-black">
                                <Layers size={10} className="inline mr-1 text-slate-400" /> {cls}
                              </td>
                            </tr>
                            {cats.map(cat => (
                              <tr key={cat.nome} className="hover:bg-slate-50/50">
                                <td className="px-6 py-2 text-slate-650 font-medium">{cat.nome}</td>
                                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                  <td key={m} className="px-3 py-2 text-center font-mono text-slate-600">
                                    {cat.valores[m] > 0 ? formatCentavos(cat.valores[m]) : "—"}
                                  </td>
                                ))}
                                <td className="px-4 py-2 text-right font-bold text-slate-700 font-mono">{formatCentavos(cat.total)}</td>
                              </tr>
                            ))}
                            {/* Subtotal da Classificação */}
                            <tr className="bg-slate-50/70 font-semibold text-slate-700 border-t border-b border-slate-200">
                              <td className="px-4 py-2 text-[10px] uppercase font-black tracking-wide text-slate-550 pl-6">
                                Subtotal {cls}
                              </td>
                              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                <td key={m} className="px-3 py-2 text-center font-mono font-bold text-slate-700">
                                  {subSomaMeses[m] > 0 ? formatCentavos(subSomaMeses[m]) : "—"}
                                </td>
                              ))}
                              <td className="px-4 py-2 text-right font-black font-mono text-slate-800">{formatCentavos(subTotalCls)}</td>
                            </tr>
                          </React.Fragment>
                        );
                      })}

                      {/* Total Geral Despesas */}
                      <tr className="bg-rose-50/70 font-black text-rose-800 border-t-2 border-rose-200">
                        <td className="px-4 py-3 text-xs uppercase tracking-wide">Total Geral Despesas</td>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => {
                          const somaMes = Object.values(pivotData.despesas).reduce((sum, cat) => sum + cat.valores[m], 0);
                          return (
                            <td key={m} className="px-3 py-3 text-center font-mono">
                              {somaMes > 0 ? formatCentavos(somaMes) : "—"}
                            </td>
                          );
                        })}
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {formatCentavos(Object.values(pivotData.despesas).reduce((sum, cat) => sum + cat.total, 0))}
                        </td>
                      </tr>

                      {/* 3. RESULTADO OPERACIONAL LÍQUIDO */}
                      <tr className="bg-slate-900 text-white font-black text-sm border-t-4 border-slate-950">
                        <td className="px-4 py-3.5 uppercase tracking-wider text-xs">Resultado Operacional Líquido</td>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => {
                          const recMes = pivotOrganizado.receitas.reduce((sum, cat) => sum + cat.valores[m], 0);
                          const desMes = Object.values(pivotData.despesas).reduce((sum, cat) => sum + cat.valores[m], 0);
                          const saldoMes = recMes - desMes;
                          return (
                            <td key={m} className={`px-3 py-3.5 text-center font-mono ${saldoMes >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                              {saldoMes < 0 ? "-" : ""}{formatCentavos(Math.abs(saldoMes))}
                            </td>
                          );
                        })}
                        <td className={`px-4 py-3.5 text-right font-mono ${totals.liquido >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                          {totals.liquido < 0 ? "-" : ""}{formatCentavos(Math.abs(totals.liquido))}
                        </td>
                      </tr>

                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ─── VISÃO: AUDITORIA BANCÁRIA ─────────────────────────────────── */}
            {activeTab === "auditoria" && (
              <div className="space-y-6">
                
                {auditoriaStats.length === 0 ? (
                  <div className="bg-white p-12 rounded-xl border border-slate-200 text-center flex flex-col items-center gap-3">
                    <AlertCircle size={40} className="text-slate-350" />
                    <span className="text-xs font-black text-slate-500 uppercase tracking-wider">Nenhum extrato importado localizado para o ano de {selAno}</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-6">
                    {auditoriaStats.map(conta => {
                      const totalConciliadasAno = conta.meses.reduce((sum, m) => sum + m.conciliadas + m.ignoradas, 0);
                      const totalTransacoesAno = conta.meses.reduce((sum, m) => sum + m.total, 0);
                      const taxaConciliacaoAno = totalTransacoesAno > 0 ? (totalConciliadasAno / totalTransacoesAno) * 100 : 100;

                      return (
                        <div key={conta.nome} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs flex flex-col">
                          
                          {/* Topo da Conta */}
                          <div className="bg-slate-900 text-white px-5 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <CheckCircle size={18} className={taxaConciliacaoAno >= 95 ? "text-emerald-400" : "text-amber-400"} />
                              <h4 className="font-bold text-sm uppercase tracking-wide font-black">{conta.nome}</h4>
                            </div>
                            <div className="text-right">
                              <span className="text-[10px] text-slate-400 uppercase font-black tracking-wider block">Eficiência de Conciliação</span>
                              <strong className="text-base font-black font-mono">{taxaConciliacaoAno.toFixed(1)}%</strong>
                            </div>
                          </div>

                          {/* Grid dos Meses */}
                          <div className="p-5 overflow-x-auto">
                            <table className="w-full text-left border-collapse min-w-[700px] text-xs">
                              <thead>
                                <tr className="border-b border-slate-200 pb-2">
                                  <th className="pb-2 text-[10px] font-black uppercase text-slate-400">Mês</th>
                                  <th className="pb-2 text-[10px] font-black uppercase text-slate-400 text-center">Transações no Banco</th>
                                  <th className="pb-2 text-[10px] font-black uppercase text-slate-400 text-center">Conciliadas / Ignoradas</th>
                                  <th className="pb-2 text-[10px] font-black uppercase text-slate-400 text-center">Status de Saúde</th>
                                  <th className="pb-2 text-[10px] font-black uppercase text-slate-400 text-right w-24">Taxa Match</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100 font-semibold">
                                {conta.meses.map(m => {
                                  const nomesMeses = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
                                  const total = m.total;
                                  const conciliadas = m.conciliadas + m.ignoradas;
                                  const taxa = total > 0 ? (conciliadas / total) * 100 : 100;

                                  let badgeStatus = null;
                                  if (total === 0) {
                                    badgeStatus = <span className="bg-slate-100 text-slate-500 font-bold px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wide">Sem Extrato</span>;
                                  } else if (taxa >= 98) {
                                    badgeStatus = <span className="bg-emerald-50 text-emerald-600 border border-emerald-100 font-bold px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wide">100% Conciliado</span>;
                                  } else if (taxa >= 80) {
                                    badgeStatus = <span className="bg-amber-50 text-amber-600 border border-amber-100 font-bold px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wide">Conciliação Parcial</span>;
                                  } else {
                                    badgeStatus = <span className="bg-rose-50 text-rose-600 border border-rose-100 font-bold px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wide">Atrasada</span>;
                                  }

                                  return (
                                    <tr key={m.mes} className="hover:bg-slate-50/50 font-medium">
                                      <td className="py-2.5 font-bold text-slate-700">{nomesMeses[m.mes - 1]}</td>
                                      <td className="py-2.5 text-center font-mono font-bold text-slate-650">{total}</td>
                                      <td className="py-2.5 text-center font-mono text-slate-600">{conciliadas}</td>
                                      <td className="py-2.5 text-center">{badgeStatus}</td>
                                      <td className={`py-2.5 text-right font-black font-mono ${taxa >= 90 ? "text-emerald-600" : "text-slate-655"}`}>
                                        {total > 0 ? `${taxa.toFixed(1)}%` : "—"}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>

                        </div>
                      );
                    })}
                  </div>
                )}

              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
